// src/redux/slices/recipesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

const CACHE_TTL_MS = 30000;

// Async thunk to fetch all recipes
export const fetchRecipes = createAsyncThunk(
  "recipes/fetch",
  async () => {
    const response = await api.get("/vendor/recipes");
    return response.data.recipes; // return only array
  },
  {
    condition: (arg = {}, { getState }) => {
      if (arg?.force) return true;
      const state = getState()?.recipes;
      if (state?.loading) return false;
      if (state?._lastFetchKind !== "all") return true;
      if (!state?.items?.length || !state?._lastFetchAt) return true;
      return Date.now() - state._lastFetchAt > CACHE_TTL_MS;
    },
  }
);

// Server-side paginated fetch (non-breaking: separate thunk)
export const fetchRecipesPaginated = createAsyncThunk(
  "recipes/fetchPaginated",
  async ({ page = 0, limit = 20, q = "", sort = "date" } = {}) => {
    const response = await api.get("/vendor/recipes", {
      params: { page, limit, q, sort },
    });
    return {
      items: response.data.recipes || [],
      total: response.data.total || 0,
      page: response.data.page || 0,
      limit: response.data.limit || limit,
      q,
      sort,
    };
  }
);

const recipesSlice = createSlice({
  name: "recipes",
  initialState: {
    items: [],
    loading: false,
    error: null,
    total: 0,
    page: 0,
    limit: 20,
    q: "",
    sort: "date",
    _lastFetchAt: 0,
    _lastFetchKind: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecipes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecipes.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state._lastFetchAt = Date.now();
        state._lastFetchKind = "all";
      })
      .addCase(fetchRecipes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Error fetching recipes";
      })
      .addCase(fetchRecipesPaginated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecipesPaginated.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.q = action.payload.q;
        state.sort = action.payload.sort;
        state._lastFetchAt = Date.now();
        state._lastFetchKind = "paginated";
      })
      .addCase(fetchRecipesPaginated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Error fetching recipes";
      });
  },
});

export default recipesSlice.reducer;
